import { WhatsNewArticle } from "../types/whatsNew.types";

export interface TableCellData {
  type: "text" | "component" | "actions";
  content: string | any;
  style?: React.CSSProperties;
  metadata?: Record<string, any>;
}

export class WhatsNewTableService {
  static formatArticleForTable(
    article: WhatsNewArticle
  ): Record<string, TableCellData> {
    return {
      featureTitle: this.createFeatureTitleCell(article.title),
      featureDescription: this.createFeatureDescriptionCell(article.body),
      visibility: this.createVisibilityCell(article),
      updates: this.createUpdateTypeCell(article.featureUpdateType),
      module: this.createModuleCell(article.module),
      date: this.createDateCell(article.createdAt),
      test: this.createTestActionsCell(article),
      actions: this.createActionButtonsCell(article),
    };
  }

  private static createFeatureTitleCell(title: string): TableCellData {
    return {
      type: "text",
      content: title,
      style: this.getTitleTextStyle(),
      metadata: { containerStyle: this.getCellContainerStyle() },
    };
  }

  private static createFeatureDescriptionCell(body?: string): TableCellData {
    return {
      type: "component",
      content: body || "No description",
      style: this.getDescriptionTextStyle(),
      metadata: {
        containerStyle: this.getCellContainerStyle(),
        useHtml: true,
      },
    };
  }

  private static createVisibilityCell(article: WhatsNewArticle): TableCellData {
    return {
      type: "component",
      content: "visibility-toggle",
      metadata: {
        articleId: article.docId,
        visibility: article.visibility,
        containerStyle: this.getCellContainerStyle(),
      },
    };
  }

  private static createUpdateTypeCell(
    featureUpdateType?: string
  ): TableCellData {
    return {
      type: "text",
      content: featureUpdateType || "Minor",
      metadata: { containerStyle: this.getCellContainerStyle() },
    };
  }

  private static createModuleCell(module: string | string[]): TableCellData {
    const modules = Array.isArray(module)
      ? module
      : module
        ? [module]
        : ["General"];
    return {
      type: "component",
      content: "module-tags",
      metadata: {
        modules,
        containerStyle: this.getModuleContainerStyle(),
      },
    };
  }

  private static createDateCell(createdAt: any): TableCellData {
    const dateText = createdAt
      ? new Date(createdAt.toDate()).toLocaleDateString()
      : "No date";

    return {
      type: "text",
      content: dateText,
      style: { fontSize: "12px", fontWeight: "500" },
      metadata: { containerStyle: this.getCellContainerStyle() },
    };
  }

  private static createTestActionsCell(
    article: WhatsNewArticle
  ): TableCellData {
    return {
      type: "component",
      content: "test-actions",
      metadata: {
        articleId: article.docId,
        videoUrl: article.productVideo,
        containerStyle: this.getTestActionsStyle(),
      },
    };
  }

  private static createActionButtonsCell(
    article: WhatsNewArticle
  ): TableCellData {
    return {
      type: "actions",
      content: "edit-delete",
      metadata: {
        articleId: article.docId,
        containerStyle: this.getCellContainerStyle(),
        actionStyle: this.getActionButtonsStyle(),
      },
    };
  }

  // Style helpers
  private static getCellContainerStyle(): React.CSSProperties {
    return {
      display: "flex",
      alignItems: "center",
      justifyContent: "flex-start",
      minHeight: "80px",
    };
  }

  private static getTitleTextStyle(): React.CSSProperties {
    return {
      maxWidth: "250px",
      overflow: "hidden",
      display: "-webkit-box",
      WebkitLineClamp: 3,
      WebkitBoxOrient: "vertical",
      lineHeight: "1.4",
      fontSize: "14px",
      fontWeight: "500",
      wordWrap: "break-word",
      whiteSpace: "normal",
      textAlign: "left",
    };
  }

  private static getDescriptionTextStyle(): React.CSSProperties {
    return {
      maxWidth: "400px",
      overflow: "hidden",
      display: "-webkit-box",
      WebkitLineClamp: 3,
      WebkitBoxOrient: "vertical",
      lineHeight: "1.4",
      fontSize: "14px",
      color: "#666",
      wordWrap: "break-word",
      whiteSpace: "normal",
      textAlign: "left",
    };
  }

  private static getModuleContainerStyle(): React.CSSProperties {
    return {
      display: "flex",
      flexWrap: "wrap",
      gap: "4px",
      alignItems: "center",
      justifyContent: "flex-start",
      minHeight: "80px",
    };
  }

  private static getTestActionsStyle(): React.CSSProperties {
    return {
      display: "flex",
      flexDirection: "row",
      gap: "16px",
      alignItems: "center",
      justifyContent: "flex-start",
      minHeight: "80px",
    };
  }

  private static getActionButtonsStyle(): React.CSSProperties {
    return {
      display: "flex",
      gap: "8px",
      alignItems: "center",
    };
  }

  static getLinkStyle(): React.CSSProperties {
    return {
      fontSize: "12px",
      color: "#731DCF",
      cursor: "pointer",
      fontWeight: "bold",
    };
  }
}
